// guide.ts — the guided-segmentation editor's pure state (ARCHITECTURE.md §10
// Viewer, §6 POST …/segment mode "guided"). The draft is the wire Guide: named
// segments, each with seed points the user tapped on the model and/or a box
// the user dragged around it (splat/boxGizmo.ts), plus exclude points. The helpers here edit it immutably, check it is ready to send, give
// each segment its colour, and build the cloud the model card shows while
// guiding: every gaussian tinted by the segment the server assigned it to
// (others dimmed), plus one bright marker splat per seed. Marker splats carry
// ids from MARKER_ID_BASE up, so a tap that picks one maps back to its seed
// (tapping a seed removes it).

import type { BoxM, Guide, GuideSegment, SegmentsResult, Vec3M } from '../api';
import { LABEL_OBJECT, LABEL_PLANE } from '../splat/format';
import type { SplatCloud } from '../splat/format';

// Where taps go: a segment's seeds, or the exclude points.
export type SeedTarget = { kind: 'segment'; id: number } | { kind: 'exclude' };

// Mirrors core/segdata's guide limits and plane tolerance.
export const MAX_SEGMENTS = 32;
export const MAX_SEEDS_PER_SEGMENT = 64;
export const MAX_EXCLUDE = 256;
export const MAX_NAME_LEN = 64;
export const PLANE_TOL_M = 0.004;

// Categorical segment colours (distinct from the measure tool's teal); the
// exclude markers are slate.
export const SEGMENT_COLORS = ['#f97316', '#3b82f6', '#a855f7', '#22c55e', '#eab308', '#ec4899', '#06b6d4', '#ef4444'];
export const EXCLUDE_COLOR = '#64748b';

export const MARKER_ID_BASE = 0xf0000000;
const MARKER_SIGMA_M = 0.0025;

export function segmentColor(index: number): string {
  return SEGMENT_COLORS[((index % SEGMENT_COLORS.length) + SEGMENT_COLORS.length) % SEGMENT_COLORS.length];
}

export function hexToRgb(hex: string): [number, number, number] {
  const v = parseInt(hex.slice(1), 16);
  return [((v >> 16) & 255) / 255, ((v >> 8) & 255) / 255, (v & 255) / 255];
}

export function emptyGuide(): Guide {
  return { segments: [{ id: 1, name: 'Segment 1', seeds: [] }], exclude: [] };
}

// draftFromGuide copies a stored guide into an editable draft (a fresh
// one-segment draft when there is none).
export function draftFromGuide(g: Guide | null | undefined): Guide {
  if (!g || g.segments.length === 0) return emptyGuide();
  return {
    segments: g.segments.map((s) => ({
      id: s.id,
      name: s.name,
      seeds: (s.seeds ?? []).map((p) => [...p] as Vec3M),
      ...(s.box ? { box: copyBox(s.box) } : {}),
    })),
    exclude: (g.exclude ?? []).map((p) => [...p] as Vec3M),
  };
}

// addSegment appends "Segment N" with the next free id.
export function addSegment(g: Guide): { guide: Guide; id: number } {
  const id = g.segments.reduce((m, s) => Math.max(m, s.id), 0) + 1;
  return { guide: { ...g, segments: [...g.segments, { id, name: `Segment ${id}`, seeds: [] }] }, id };
}

function copyBox(b: BoxM): BoxM {
  return { min: [b.min[0], b.min[1], b.min[2]], max: [b.max[0], b.max[1], b.max[2]] };
}

// setSegmentBox gives segment id the box (null removes it).
export function setSegmentBox(g: Guide, id: number, box: BoxM | null): Guide {
  return {
    ...g,
    segments: g.segments.map((s) => {
      if (s.id !== id) return s;
      return box ? { ...s, box: copyBox(box) } : { id: s.id, name: s.name, seeds: s.seeds };
    }),
  };
}

export function segmentBox(g: Guide, id: number): BoxM | null {
  return g.segments.find((s) => s.id === id)?.box ?? null;
}

export function removeSegment(g: Guide, id: number): Guide {
  return { ...g, segments: g.segments.filter((s) => s.id !== id) };
}

export function renameSegment(g: Guide, id: number, name: string): Guide {
  return { ...g, segments: g.segments.map((s) => (s.id === id ? { ...s, name } : s)) };
}

function mapTarget(g: Guide, t: SeedTarget, fn: (pts: Vec3M[]) => Vec3M[]): Guide {
  if (t.kind === 'exclude') return { ...g, exclude: fn(g.exclude ?? []) };
  return { ...g, segments: g.segments.map((s) => (s.id === t.id ? { ...s, seeds: fn(s.seeds) } : s)) };
}

export function seedsOf(g: Guide, t: SeedTarget): Vec3M[] {
  if (t.kind === 'exclude') return g.exclude ?? [];
  return g.segments.find((s) => s.id === t.id)?.seeds ?? [];
}

// addSeed appends p to the target. It refuses, with a reason, a point on the
// support surface (no segment can contain it) and a full target.
export function addSeed(g: Guide, t: SeedTarget, p: Vec3M): { guide: Guide } | { error: string } {
  if (p[2] < PLANE_TOL_M) return { error: 'That is the support surface — tap the object itself.' };
  if (t.kind === 'segment' && !g.segments.some((s) => s.id === t.id)) return { error: 'That segment no longer exists.' };
  const cap = t.kind === 'exclude' ? MAX_EXCLUDE : MAX_SEEDS_PER_SEGMENT;
  if (seedsOf(g, t).length >= cap) return { error: `At most ${cap} points here.` };
  return { guide: mapTarget(g, t, (pts) => [...pts, [p[0], p[1], p[2]]]) };
}

export function removeSeed(g: Guide, t: SeedTarget, index: number): Guide {
  return mapTarget(g, t, (pts) => pts.filter((_, i) => i !== index));
}

export function undoSeed(g: Guide, t: SeedTarget): Guide {
  return mapTarget(g, t, (pts) => pts.slice(0, -1));
}

export function clearSeeds(g: Guide): Guide {
  return { segments: g.segments.map((s) => ({ ...s, seeds: [] })), exclude: [] };
}

// guideProblem says why the draft can't be sent yet, or null when it can.
export function guideProblem(g: Guide): string | null {
  if (g.segments.length === 0) return 'Add a segment.';
  if (g.segments.length > MAX_SEGMENTS) return `At most ${MAX_SEGMENTS} segments.`;
  const names = new Set<string>();
  for (const s of g.segments) {
    const name = s.name.trim();
    if (!name) return 'Every segment needs a name.';
    if ([...name].length > MAX_NAME_LEN) return `Names are at most ${MAX_NAME_LEN} characters.`;
    if (names.has(name.toLowerCase())) return `Two segments are called "${name}".`;
    names.add(name.toLowerCase());
    if (s.seeds.length === 0 && !s.box) return `Tap the model to seed "${name}", or draw a box around it.`;
  }
  return null;
}

// toWire trims names and omits an empty exclude list.
export function toWire(g: Guide): Guide {
  const exclude = g.exclude ?? [];
  return { segments: g.segments.map((s) => ({ ...s, name: s.name.trim() })), ...(exclude.length ? { exclude } : {}) };
}

// sameGuide compares two guides by content (the draft vs the stored one).
export function sameGuide(a: Guide | null | undefined, b: Guide | null | undefined): boolean {
  return JSON.stringify(a ? toWire(a) : null) === JSON.stringify(b ? toWire(b) : null);
}

interface Marker {
  pos: Vec3M;
  rgb: [number, number, number];
}

// markersOf lists the draft's seed markers in id order: segments' seeds in
// guide order, then exclude points.
function markersOf(g: Guide): Marker[] {
  const out: Marker[] = [];
  g.segments.forEach((s, i) => {
    const rgb = hexToRgb(segmentColor(i));
    for (const p of s.seeds) out.push({ pos: p, rgb });
  });
  const ex = hexToRgb(EXCLUDE_COLOR);
  for (const p of g.exclude ?? []) out.push({ pos: p, rgb: ex });
  return out;
}

// markerSeed maps a picked marker splat id back to its seed.
export function markerSeed(g: Guide, id: number): { target: SeedTarget; index: number } | null {
  if (id < MARKER_ID_BASE) return null;
  let k = id - MARKER_ID_BASE;
  for (const s of g.segments) {
    if (k < s.seeds.length) return { target: { kind: 'segment', id: s.id }, index: k };
    k -= s.seeds.length;
  }
  const ex = g.exclude ?? [];
  return k < ex.length ? { target: { kind: 'exclude' }, index: k } : null;
}

// segmentIndexById maps each gaussian id to the index (in guide order) of
// the segment the server put it in.
export function segmentIndexById(result: SegmentsResult | null): Map<number, number> {
  const m = new Map<number, number>();
  if (!result) return m;
  result.segments.forEach((s, i) => {
    for (const id of s.gaussian_ids ?? []) m.set(id, i);
  });
  return m;
}

// guidedDisplayCloud returns a copy of c with segment members blended 55%
// toward their segment's colour, everything else dimmed, and one marker
// splat per draft seed appended. Colours follow the draft: a server segment
// is drawn in the colour of the draft segment with its id (untinted once the
// user removed it from the draft).
export function guidedDisplayCloud(c: SplatCloud, draft: Guide, result: SegmentsResult | null): SplatCloud {
  const byId = segmentIndexById(result);
  const draftIndex = new Map(draft.segments.map((s, i) => [s.id, i]));
  const colourOf = (result?.segments ?? []).map((s) => draftIndex.get(s.id) ?? -1);
  const rgbs = SEGMENT_COLORS.map(hexToRgb);
  const markers = markersOf(draft);
  const n = c.count + markers.length;
  const out: SplatCloud = {
    count: n,
    ids: new Uint32Array(n),
    pos: new Float32Array(n * 3),
    logScale: new Float32Array(n * 3),
    rot: new Float32Array(n * 4),
    color: new Float32Array(n * 3),
    opacity: new Float32Array(n),
    label: new Uint32Array(n),
  };
  out.ids.set(c.ids.subarray(0, c.count));
  out.pos.set(c.pos.subarray(0, c.count * 3));
  out.logScale.set(c.logScale.subarray(0, c.count * 3));
  out.rot.set(c.rot.subarray(0, c.count * 4));
  out.opacity.set(c.opacity.subarray(0, c.count));
  out.label.set(c.label.subarray(0, c.count));
  for (let i = 0; i < c.count; i++) {
    const s = byId.get(c.ids[i]);
    const ci = s === undefined ? -1 : colourOf[s];
    for (let k = 0; k < 3; k++) {
      const v = c.color[i * 3 + k];
      if (ci >= 0) out.color[i * 3 + k] = 0.45 * v + 0.55 * rgbs[ci % rgbs.length][k];
      else out.color[i * 3 + k] = c.label[i] === LABEL_PLANE ? 0.7 * v : 0.45 * v;
    }
  }
  const ls = Math.log(MARKER_SIGMA_M);
  markers.forEach((m, k) => {
    const i = c.count + k;
    out.ids[i] = MARKER_ID_BASE + k;
    out.pos.set(m.pos, i * 3);
    out.logScale.set([ls, ls, ls], i * 3);
    out.rot.set([1, 0, 0, 0], i * 4);
    out.color.set(m.rgb, i * 3);
    out.opacity[i] = 1;
    out.label[i] = LABEL_OBJECT;
  });
  return out;
}

// describeTarget names a tap target for the hint line.
export function describeTarget(g: Guide, t: SeedTarget): string {
  if (t.kind === 'exclude') return 'Exclude';
  const s: GuideSegment | undefined = g.segments.find((x) => x.id === t.id);
  return s ? s.name.trim() || `Segment ${s.id}` : 'segment';
}
